/**
 * Apple Wallet "Add to Wallet" modal — Thredd web push provisioning.
 *
 * Flow:
 *   INPUT      enter last 4 digits → Continue
 *   SUBMITTING ensureFingerprintId + validateCardV2 → createWalletProvisioningSession
 *   REDIRECT   full-page redirect to the shared provisioning subdomain, which
 *              runs the Thredd SDK (same handoff as the Google Pay modal)
 *   ERROR      show error message + retry
 *
 * Props:
 *   token      {string}   9-digit card token
 *   onClose    {function} called when modal is dismissed
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../api';
import { ensureFingerprintId } from '../utils/fingerprintManager';
import './apple-wallet-provisioning.css';

const sendTelemetry = (api, correlationId, event, payload) => {
    if (!api?.reportWalletProvisioningLog) return;
    api.reportWalletProvisioningLog(correlationId, event, payload).catch(() => {});
};

const STEPS = {
    INPUT:      'input',
    SUBMITTING: 'submitting',
    ERROR:      'error',
};

const AppleWalletProvisioningModal = ({ token, onClose }) => {
    const { t } = useTranslation();
    const api = useRef(API()).current;

    const [step, setStep] = useState(STEPS.INPUT);
    const [last4, setLast4] = useState('');
    const [last4Error, setLast4Error] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const dialogRef = useRef(null);
    // Client-side correlation ID — passed as the ticket to the telemetry log
    // endpoint, which accepts any string without DB validation, so we get a
    // consistent ID across all events for one provisioning attempt without
    // needing a server-side session.
    const correlationId = useRef(
        typeof window.crypto?.randomUUID === 'function'
            ? `apple-${window.crypto.randomUUID()}`
            : `apple-${Date.now()}-${Math.random().toString(36).slice(2)}`
    ).current;

    useEffect(() => {
        if (dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleClose = useCallback(() => {
        if (dialogRef.current && dialogRef.current.open) dialogRef.current.close();
        onClose();
    }, [onClose]);

    const handleDialogCancel = (e) => {
        e.preventDefault();
    };

    const isLast4Valid = (val) => /^\d{4}$/.test(val);

    const handleLast4Change = (e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
        setLast4(val);
        setLast4Error('');
    };

    const handleContinue = async () => {
        if (!isLast4Valid(last4)) {
            setLast4Error(t('appleWallet.last4Invalid', 'Enter exactly 4 digits'));
            return;
        }

        setStep(STEPS.SUBMITTING);
        sendTelemetry(api, correlationId, 'provision_initiated', { userAgent: navigator.userAgent });
        try {
            const fp = await ensureFingerprintId(api);
            const cardResponse = await api.validateCardV2(token, last4, fp);
            if (cardResponse.error) throw new Error(cardResponse.error);
            const cardId = cardResponse.cardId || cardResponse.card_id;
            if (!cardId) throw new Error(t('appleWallet.cardValidationFailed', 'Card validation failed. Please check your card details.'));
            sendTelemetry(api, correlationId, 'card_validated', { cardId });

            // Hand off to the shared provisioning subdomain, the same as Google
            // Pay. The Apple SDK runs there, so its popup is not blocked by the
            // brand page.
            const sessionResponse = await api.createWalletProvisioningSession({
                provider: 'apple',
                cardId,
                fingerprintId: fp,
                returnPath: window.location.pathname + window.location.hash,
            });
            if (sessionResponse.error || !sessionResponse.provisioningUrl) {
                throw new Error(sessionResponse.error || t('appleWallet.genericError', 'Something went wrong. Please try again.'));
            }
            sendTelemetry(api, correlationId, 'session_created', { cardId, ticket: sessionResponse.ticket });

            window.location.assign(sessionResponse.provisioningUrl);
        } catch (err) {
            console.error('Apple Wallet provision error:', err);
            sendTelemetry(api, correlationId, 'provision_error', { message: err.message });
            setErrorMsg(err.message || t('appleWallet.genericError', 'Something went wrong. Please try again.'));
            setStep(STEPS.ERROR);
        }
    };

    const handleRetry = () => {
        setErrorMsg('');
        setStep(STEPS.INPUT);
    };

    return (
        <dialog
            ref={dialogRef}
            className="appleWalletModal"
            onCancel={handleDialogCancel}
            aria-modal="true"
            aria-labelledby="apple-wallet-modal-title"
        >
            <div className="appleWalletModal__container">
                <div className="appleWalletModal__content">
                    <button
                        className="appleWalletModal__close"
                        onClick={handleClose}
                        aria-label={t('appleWallet.close', 'Close')}
                    >×</button>

                    <div className="appleWalletModal__main">
                        <div className="appleWalletModal__logo">
                            <img
                                className="appleWalletModal__logo-image"
                                src="/assets/apple-wallet/ApplePayLogo.svg"
                                alt={t('appleWallet.logoAlt', 'Apple Wallet')}
                            />
                        </div>

                        <h2 id="apple-wallet-modal-title" className="appleWalletModal__title">
                            {t('appleWallet.title', 'Add your Gift Card to your Apple Wallet')}
                        </h2>

                        {step === STEPS.INPUT && (
                            <div className="appleWalletModal__step appleWalletModal__step--input">
                                <p>{t('appleWallet.last4Prompt', 'Enter the last 4 digits of your card to continue.')}</p>

                                <label htmlFor="apple-wallet-last4">{t('appleWallet.last4Label', 'Last 4 digits')}</label>
                                <input
                                    id="apple-wallet-last4"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={last4}
                                    onChange={handleLast4Change}
                                    placeholder="0000"
                                    autoFocus
                                />
                                {last4Error && <span className="error">{last4Error}</span>}

                                <button
                                    className={`button appleWalletModal__cta disabled-${!isLast4Valid(last4)}`}
                                    onClick={handleContinue}
                                    disabled={!isLast4Valid(last4)}
                                >
                                    {t('appleWallet.continue', 'Continue')}
                                </button>
                            </div>
                        )}

                        {step === STEPS.SUBMITTING && (
                            <div className="appleWalletModal__step appleWalletModal__step--submitting">
                                <div className="loader spinner" />
                            </div>
                        )}

                        {step === STEPS.ERROR && (
                            <div className="appleWalletModal__step appleWalletModal__step--error">
                                <p className="error">{errorMsg}</p>
                                <button className="button appleWalletModal__cta" onClick={handleRetry}>
                                    {t('appleWallet.tryAgain', 'Try again')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="appleWalletModal__notice">
                    {t('appleWallet.popupNotice', 'Pop-ups are required to continue with Apple Wallet. If disabled, please enable them on your browser.')}
                </div>
            </div>
        </dialog>
    );
};

export default AppleWalletProvisioningModal;
